'use client';

/**
 * Quiz catalog summary card.
 *
 * Module: components/quizzes/QuizCard
 * Layer:  component
 *
 * Purpose:
 *     Renders one {@link QuizSummary} as a catalog card: title, optional
 *     description, the owning course (when present), a meta row showing
 *     question count and time limit, a passing-score badge, and a "Start quiz"
 *     link to the taking page. Sub-pieces are factored out so every function
 *     clears the CC<=5 / <=60-line gates.
 *
 * AI context:
 *     - Entry points called by: src/app/quizzes/quiz-grid (the catalog grid).
 *     - Depends on: next/link, lib/quizzes (types), lib/i18n.
 *     - Side effects: none; purely presentational.
 *     - Invariants: presentational only, never fetches; every visible string
 *       comes from the i18n catalogue.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - All fields are backend-controlled text rendered through React (escaped);
 *       no HTML is emitted and the id is a URL-safe numeric segment.
 */

import type { Route } from 'next';
import Link from 'next/link';
import type React from 'react';
import { useTranslation } from '@/lib/i18n';
import type { QuizSummary } from '@/lib/quizzes';

/** Props for {@link QuizCard}. */
export interface QuizCardProps {
  readonly quiz: QuizSummary;
}

/** Build the typed taking-page route for a quiz id. */
function quizHref(id: number): Route {
  // WHY: typedRoutes types this template as Route; the cast documents the id is
  // an app-owned, URL-safe numeric segment.
  return `/quizzes/${String(id)}` as Route;
}

/** Question-count label with singular/plural handling. */
function QuestionCount(props: { readonly count: number }): React.JSX.Element {
  const { t } = useTranslation();
  const label =
    props.count === 1
      ? t('quizzes.card.questionCountOne')
      : t('quizzes.card.questionCount', { count: props.count });
  return <span>{label}</span>;
}

/** Time-limit label, falling back to an "untimed" message. */
function TimeLimit(props: { readonly minutes: number | null }): React.JSX.Element {
  const { t } = useTranslation();
  const label =
    props.minutes === null
      ? t('quizzes.card.noTimeLimit')
      : t('quizzes.card.timeLimit', { minutes: props.minutes });
  return <span>{label}</span>;
}

/** Owning-course line, rendered only when the quiz belongs to a course. */
function CourseLine(props: { readonly quiz: QuizSummary }): React.JSX.Element | null {
  const { t } = useTranslation();
  const { quiz } = props;
  if (quiz.course_title === null) {
    return null;
  }
  return (
    <p className='mt-1 text-sm font-medium opacity-70'>
      {t('quizzes.card.inCourse', { course: quiz.course_title })}
    </p>
  );
}

/**
 * Catalog card for a single quiz.
 *
 * @param props - The quiz summary to render.
 * @returns The card element linking to `/quizzes/{id}`.
 */
export function QuizCard(props: QuizCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const { quiz } = props;
  return (
    <div className='flex flex-col rounded-2xl border border-border p-6 transition-shadow hover:shadow-lg'>
      <div className='flex items-start justify-between gap-3'>
        <h3 className='line-clamp-2 text-lg font-semibold'>{quiz.title}</h3>
        <span className='shrink-0 rounded-full border border-border px-2 py-1 text-xs font-semibold'>
          {t('quizzes.card.passingScore', { score: quiz.passing_score })}
        </span>
      </div>
      <CourseLine quiz={quiz} />
      {quiz.description === null ? null : (
        <p className='mt-2 line-clamp-2 flex-1 text-sm opacity-70'>{quiz.description}</p>
      )}
      <div className='mt-4 flex items-center gap-4 text-sm opacity-70'>
        <QuestionCount count={quiz.question_count} />
        <TimeLimit minutes={quiz.time_limit_minutes} />
      </div>
      <Link
        href={quizHref(quiz.id)}
        className='mt-4 inline-block rounded-lg border border-border px-4 py-2 text-center text-sm font-medium transition-colors hover:bg-foreground/5'
      >
        {t('quizzes.card.start')}
      </Link>
    </div>
  );
}
